/**
 * Holster wallet proxies (PDP + cart drawer)
 * - Keep Apple Pay / Google Pay proxy buttons visible under ATC / Zur Kasse
 * - Hide Shopify "Jetzt zum Checkout" (unbranded buy_it_now) and more-options link
 * - Wire proxy clicks to real Shopify wallet buttons when available
 * - Never re-enable PayPal on restricted products
 *
 * Perf: scoped MutationObserver (hosts only), no document-wide subtree scanning storms.
 */
(function () {
  'use strict';

  var HOST_SEL = '.holster-pdp-wallets, .holster-cart-wallets';
  var MORE_SEL =
    'more-payment-options-link, .shopify-payment-button__more-options, [class*="more-payment"], a[href="#more-payment-options"]';
  var UNBRANDED_SEL =
    '.shopify-payment-button__button--unbranded, button.shopify-payment-button__button--unbranded, [data-testid="ShopifyPay-button"]';

  function hay(el) {
    if (!el) return '';
    return [
      el.className,
      el.id,
      el.getAttribute && el.getAttribute('aria-label'),
      el.getAttribute && el.getAttribute('title'),
      el.getAttribute && el.getAttribute('data-testid'),
      el.getAttribute && el.getAttribute('name'),
      el.tagName,
      el.textContent,
    ]
      .join(' ')
      .toLowerCase();
  }

  function matchWallet(el, kind) {
    var h = hay(el);
    if (kind === 'apple') {
      return h.indexOf('apple-pay') !== -1 || h.indexOf('apple pay') !== -1 || h.indexOf('applepay') !== -1;
    }
    if (kind === 'google') {
      return (
        h.indexOf('google-pay') !== -1 ||
        h.indexOf('google pay') !== -1 ||
        h.indexOf('googlepay') !== -1 ||
        (h.indexOf('gpay') !== -1 && h.indexOf('paypal') === -1)
      );
    }
    return false;
  }

  function walk(node, fn) {
    if (!node) return;
    fn(node);
    if (node.querySelectorAll) {
      node.querySelectorAll('*').forEach(function (child) {
        if (child.shadowRoot) walk(child.shadowRoot, fn);
      });
    }
    if (node.shadowRoot) walk(node.shadowRoot, fn);
  }

  function hideNode(el) {
    if (!el) return;
    el.setAttribute('data-holster-wallet-hidden', '1');
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
    el.style.setProperty('height', '0', 'important');
    el.style.setProperty('overflow', 'hidden', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
    el.setAttribute('aria-hidden', 'true');
  }

  function hideUnwanted(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(MORE_SEL).forEach(hideNode);
    scope.querySelectorAll(UNBRANDED_SEL).forEach(hideNode);
    scope.querySelectorAll('button, a, [role="button"]').forEach(function (el) {
      var t = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (el.getAttribute('data-holster-wallet-proxy')) return;
      var isCheckoutLabel =
        t === 'jetzt zum checkout' ||
        t.indexOf('jetzt zum checkout') !== -1 ||
        t === 'buy it now' ||
        t === 'buy with shop' ||
        (t.indexOf('shop pay') !== -1 && t.indexOf('google') === -1 && t.indexOf('apple') === -1);
      if (isCheckoutLabel) hideNode(el);
    });
  }

  function findClickable(scope, kind) {
    var found = null;
    walk(scope, function (root) {
      if (found || !root.querySelectorAll) return;
      root.querySelectorAll('button, a, [role="button"], div[tabindex]').forEach(function (el) {
        if (found) return;
        if (el.getAttribute && el.getAttribute('data-holster-wallet-proxy')) return;
        if (matchWallet(el, kind)) found = el;
      });
    });
    return found;
  }

  function activateWallet(host, kind) {
    var shopifyHost = host.querySelector('[data-holster-shopify-wallets]') || host;
    var target = findClickable(shopifyHost, kind) || findClickable(document, kind);
    if (target) {
      target.click();
      return;
    }

    var more = null;
    walk(shopifyHost, function (root) {
      if (more || !root.querySelectorAll) return;
      root.querySelectorAll(MORE_SEL + ', button, a').forEach(function (el) {
        if (more) return;
        var h = hay(el);
        if (
          h.indexOf('weitere bezahl') !== -1 ||
          h.indexOf('more payment') !== -1 ||
          h.indexOf('more-payment') !== -1 ||
          el.tagName === 'MORE-PAYMENT-OPTIONS-LINK'
        ) {
          more = el.querySelector('a, button') || el;
        }
      });
    });
    if (more) {
      var wrap = more.closest(MORE_SEL) || more;
      wrap.style.pointerEvents = 'auto';
      wrap.style.display = 'block';
      more.click();
      setTimeout(function () {
        var retry = findClickable(document, kind);
        if (retry) retry.click();
        hideUnwanted(host);
      }, 400);
      return;
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    if (host.closest('.holster-cart-wallets, .cart__ctas, cart-drawer-component, #cart-drawer, .cart-drawer')) {
      window.location.href = root + 'checkout';
      return;
    }

    var form =
      host.closest('form[action*="/cart/add"]') ||
      document.querySelector('product-form-component form[action*="/cart/add"], form[action*="/cart/add"]');
    if (!form) return;
    var fd = new FormData(form);
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd,
      credentials: 'same-origin',
    })
      .then(function () {
        window.location.href = root + 'checkout';
      })
      .catch(function () {
        form.submit();
      });
  }

  function wireHost(host) {
    hideUnwanted(host);
    walk(host, hideUnwanted);

    host.querySelectorAll('[data-holster-wallet-proxy]').forEach(function (btn) {
      if (btn.getAttribute('data-holster-wallet-wired') === '1') return;
      btn.setAttribute('data-holster-wallet-wired', '1');
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        activateWallet(host, btn.getAttribute('data-holster-wallet-proxy'));
      });
    });
  }

  function run() {
    document.querySelectorAll(HOST_SEL).forEach(wireHost);
    document
      .querySelectorAll(
        '.product-information ' + UNBRANDED_SEL + ', .product-details ' + UNBRANDED_SEL + ', .buy-buttons-block ' + UNBRANDED_SEL
      )
      .forEach(hideNode);
    document.querySelectorAll('.product-information button, .product-details button, .buy-buttons-block button').forEach(function (el) {
      if (el.getAttribute('data-holster-wallet-proxy')) return;
      var t = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (t.indexOf('jetzt zum checkout') !== -1) hideNode(el);
    });
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      run();
    });
  }

  function observeHosts() {
    if (typeof MutationObserver === 'undefined') return;
    document.querySelectorAll(HOST_SEL + ', #cart-drawer, .product-information, .buy-buttons-block').forEach(function (root) {
      if (root.getAttribute('data-holster-wallet-mo') === '1') return;
      root.setAttribute('data-holster-wallet-mo', '1');
      var mo = new MutationObserver(schedule);
      mo.observe(root, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      schedule();
      observeHosts();
    });
  } else {
    schedule();
    observeHosts();
  }
  window.addEventListener('load', function () {
    schedule();
    observeHosts();
  });
  document.addEventListener('shopify:section:load', function () {
    schedule();
    observeHosts();
  });
  document.addEventListener('cart-drawer:refreshed', schedule);
  document.addEventListener('cart:update', schedule);
  document.addEventListener('shopify:cart:lines-update', schedule);
  // Short retries for late Shopify wallet injection only (no long polling)
  [300, 1200].forEach(function (ms) {
    setTimeout(function () {
      schedule();
      observeHosts();
    }, ms);
  });
})();
